import React, {useEffect, useState, useContext, useRef} from 'react';
import {Map, List} from 'immutable';
import {convertDateAndTimeToMomentObject} from 'lib/timeUtils';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {addToast, Text, Button, Icon, Modal, Dialogue, TextField, Banner} from '@albert-io/atomic';
import {invalidatePartialInterest, genericMandarkRequest} from 'resources/mandark.resource';
import {query} from '@albert-io/json-api-framework/request/builder';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import {NewClassroomModal} from 'client/Classrooms/Teacher/NewClassroomModal/NewClassroomModal.react';
import {ContentDiscoveryContext} from 'client/components/ContentDiscovery/ContentDiscoveryProvider.react';
import {useTracking} from 'react-tracking';

import RecipientSettings from './RecipientSettings/RecipientSettings.react';
import GeneralSettings from './GeneralSettings/GeneralSettings.react';
import {ClassroomAssignmentSettings} from './RecipientSettings/RecipientSettings.types';
import {getActiveClassroomsQuery} from './settingsModal.utils';
import {SETTINGS as correctAnswerSettings} from 'client/Assignments/constants';

import './settings-modal.scss';

interface Props {
  handleClose: () => void;
  draftName?: string;
  setDraftName: (draftName) => void;
  draftNameError: boolean;
  setDraftNameError: (boolean) => void;
}

const SettingsModal = ({
  handleClose,
  draftName,
  setDraftName,
  draftNameError,
  setDraftNameError
}: Props) => {
  const [hasActiveClassrooms, setHasActiveClassrooms] = useState(true);
  const [showNewClassModal, setShowNewClassModal] = useState(false);

  // Individual classroom recipient settings
  const [selectedClassrooms, setSelectedClassrooms] = useState<
    Map<string, ClassroomAssignmentSettings>
  >(Map());
  const [showClassDropdownPopover, setShowClassDropdownPopover] = useState(false);
  const popoverRef = useRef();
  const [noSelectedClassesError, setNoSelectedClassesError] = useState('');
  const [noDueDateClassIds, setNoDueDateClassIds] = useState<List<string>>(List());
  const [showDueDateError, setShowDueDateError] = useState(false);

  // General settings
  const [hasTimeLimit, setHasTimeLimit] = useState(false);
  const [timeLimit, setTimeLimit] = useState<null | number>(null);
  const [allowLateSubmissions, setAllowLateSubmissions] = useState(false);
  const [randomizeQuestionOrder, setRandomizeQuestionOrder] = useState(false);
  const [showExplanationsOption, setShowExplanationsOption] = useState(
    correctAnswerSettings.SHOW_AFTER_EACH
  );
  const [message, setMessage] = useState('');

  // Context values
  const {
    draftAssignment,
    questionSetInDraftIds,
    setAssignmentResults,
    autoSave,
    selectedQuestionSets,
    updateDraftName,
    totalQuestionCount
  } = useContext(ContentDiscoveryContext);

  const {trackEvent} = useTracking({
    event: 'click',
    type: 'button',
    component: 'assignment settings'
  });

  useEffect(() => {
    const fetchClassrooms = async () => {
      try {
        const classroomsQuery = getActiveClassroomsQuery();
        const response = await classroomsQuery.getResourcePromise();
        if (response.size === 0) {
          setHasActiveClassrooms(false);
        }
      } catch (err) {
        addToast({
          type: 'danger',
          title: 'Error',
          message: 'Something went wrong with your request. Please try again.'
        });
      }
    };
    fetchClassrooms();
  }, []);

  // This function checks for any errors on the table (if classrooms are selected, if due dates are missing).
  // If there are no errors, the function will create the assignment.
  const saveAssignment = () => {
    trackEvent({content: 'assign work'});
    // Creates a map of the classes that do not have user inputted due dates.
    const noDueDateClassesMap = selectedClassrooms
      .filter((classroomObj) => !classroomObj?.dueDate)
      .map((classroomObj) => classroomObj?.classroomInfo.getId());
    if (!draftName) {
      setDraftNameError(true);
    } else if (selectedClassrooms.isEmpty()) {
      setNoSelectedClassesError('Select a classroom to assign work.');
    } else if (!noDueDateClassesMap.isEmpty()) {
      setNoDueDateClassIds(noDueDateClassesMap.toList());
    } else {
      createAssignment();
    }
  };

  const createAssignment = async () => {
    const assignmentPayloads = selectedClassrooms.toArray().reduce((acc, classroomObj) => {
      const payload = createAssignmentPayload(classroomObj);
      // @ts-ignore todo - type payload
      acc.push(payload);
      return acc;
    }, []);

    await genericMandarkRequest(
      'post',
      query()
        .mandarkEndpoint(['assignments_v3'])
        .customHeader({'Content-Type': 'application/vnd.api+json; ext=bulk'})
        .include('classrooms_v1.school_v5,students_v1')
        .withMeta('assignment_v3,classroom_v1,student_v1')
        .customQuery({
          meta: {
            context: {
              lti_deployment: true,
              lti_platform_name: true
            }
          }
        })
        .done(),
      {data: assignmentPayloads}
    )
      .then((assignmentResults) => {
        invalidatePartialInterest({resourcePath: ['assignments_v3']});

        // Invalidates classroom list view metadata query + index query
        invalidatePartialInterest({
          resourcePath: [
            'teachers_v1',
            masqueradeStore.getUserIdByMasqueradeState(),
            'classrooms_v1'
          ]
        });
        addToast({
          type: 'success',
          title: 'Success',
          message: 'Your assignment(s) were created!'
        });
        setAssignmentResults(assignmentResults);
        handleClose();
      })
      .catch(() => {
        addToast({
          type: 'danger',
          title: 'Error',
          message: 'Something went wrong with creating your assignment(s), please try again.'
        });
      });
  };

  const createAssignmentPayload = (classroomObj) => {
    const {
      dueDate,
      dueDateTime,
      selectedStudents,
      startDate,
      startDateTime,
      autoAssignNewEnrollees
    } = classroomObj;
    // Get from relationship query
    const questionSetIds = autoSave
      ? questionSetInDraftIds
      : selectedQuestionSets.map((question) => question.getId());
    const teacherId = masqueradeStore.getUserIdByMasqueradeState();
    // extract from classroomObj
    const studentIds = selectedStudents.reduce((acc, student) => {
      acc.push(student.getId());
      return acc;
    }, []);

    let assignmentModel = new AssignmentModelV3();

    assignmentModel = assignmentModel.updateRelationship('teacher_v1', teacherId);

    const questionSetCustomMeta = questionSetIds.reduce((result, questionSetId, index) => {
      // @ts-ignore - result can possibly be null
      return result.set(questionSetId, {position: index});
    }, Map());
    const convertedDueDate = convertDateAndTimeToMomentObject({date: dueDate, time: dueDateTime});
    const convertedStartDate = startDate
      ? convertDateAndTimeToMomentObject({date: startDate, time: startDateTime})
      : 'now';

    const classes = [classroomObj.classroomInfo.getId()];

    const settings = {
      allow_late_submissions: allowLateSubmissions,
      correct_answer_setting: showExplanationsOption,
      due_date: convertedDueDate,
      randomize_question_order: randomizeQuestionOrder,
      start_date: convertedStartDate,
      auto_assign_new_classroom_enrollees: autoAssignNewEnrollees,
      // if these are empty strings or null, the backend errors
      ...(hasTimeLimit && {time_limit: timeLimit}),
      ...(message && {message})
    };

    const classesCustomMeta = classes.reduce(
      (result, id) =>
        result.set(id, {
          settings: {
            type: 'classroom',
            settings
          }
        }),
      Map()
    );

    // draftAssignment === null means the draft name has not been made.
    // Right now, when the user submits a draft with no name the red danger toast appears.
    // Todo: Come up with another check that more accurately describes cases when draft name is chosen and show a more detailed error toast.
    if (draftAssignment !== null || autoSave === false) {
      const payload = assignmentModel
        .setName(autoSave ? draftAssignment.getName() : draftName)
        .setStartDate(convertedStartDate)
        .setDueDate(convertedDueDate)
        .setTimeLimit(hasTimeLimit ? timeLimit : null)
        .setRandomizeQuestionOrder(randomizeQuestionOrder)
        .setStatus('assigned')
        .setMessage(message)
        .setAllowLateSubmissions(allowLateSubmissions)
        .setCorrectAnswerSetting(showExplanationsOption)
        .setAutoAssignNewClassroomEnrollees(autoAssignNewEnrollees)
        .setSettings({
          type: 'default',
          settings
        })
        .updateRelationship('question_sets_v1', questionSetIds, true, questionSetCustomMeta)
        .updateRelationship('classrooms_v1', classes, true, classesCustomMeta)
        .updateRelationship('students_v1', studentIds, true)
        .getConstructedPayload();

      return payload;
    }

    return null;
  };

  return (
    <Modal ariaLabel='Settings Modal' handleClose={handleClose} dismissable={false}>
      {() => (
        <Dialogue
          className='settings-modal__container'
          inModal
          paddingLevel={0}
          size='xl'
          hideCloseBtn
        >
          <div className='u-overflow_auto'>
            <TextField
              size='regular'
              className='u-max-width_240 u-mar-l_4 u-mar-t_4 u-mar-b_2'
              value={draftName}
              placeholder='Unnamed assignment'
              onBlur={() => {
                if (autoSave) updateDraftName(draftName);
              }}
              onChange={(e) => {
                if (e.target.value) {
                  setDraftNameError(false);
                }
                setDraftName((e.target as HTMLInputElement).value);
              }}
              error={draftNameError}
              message={draftNameError && 'Provide an assignment name'}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  // if (autoSave) updateDraftName((e.target as HTMLInputElement).value);
                }
              }}
            />
            {totalQuestionCount === 0 && (
              <Banner theme='danger' className='u-mar-lr_2 u-mar-tb_4'>
                <div className='u-display_flex u-flex-direction_column u-align-items_center u-width_100pc'>
                  <Text bold color='red' className='u-mar-b_1'>
                    No assignment questions
                  </Text>
                  <Text color='red'>You cannot assign an assignment without any questions.</Text>
                </div>
              </Banner>
            )}

            <div className='assignment-section-header__title u-pad-lr_4 u-pad-t_3 u-pad-b_1 u-font-weight_bold-'>
              Recipients
            </div>
            {hasActiveClassrooms ? (
              <>
                <RecipientSettings
                  selectedClassrooms={selectedClassrooms}
                  setSelectedClassrooms={setSelectedClassrooms}
                  showClassDropdownPopover={showClassDropdownPopover}
                  setShowClassDropdownPopover={setShowClassDropdownPopover}
                  popoverRef={popoverRef}
                  noSelectedClassesError={noSelectedClassesError}
                  noDueDateClassIds={noDueDateClassIds}
                  showDueDateError={showDueDateError}
                  setShowDueDateError={setShowDueDateError}
                />
                <div className='assignment-section-header__title u-pad-lr_4 u-pad-tb_1 u-font-weight_bold-'>
                  General
                </div>
                <GeneralSettings
                  hasTimeLimit={hasTimeLimit}
                  setHasTimeLimit={setHasTimeLimit}
                  timeLimit={timeLimit}
                  setTimeLimit={setTimeLimit}
                  allowLateSubmissions={allowLateSubmissions}
                  setAllowLateSubmissions={setAllowLateSubmissions}
                  randomizeQuestionOrder={randomizeQuestionOrder}
                  setRandomizeQuestionOrder={setRandomizeQuestionOrder}
                  showExplanationsOption={showExplanationsOption}
                  setShowExplanationsOption={setShowExplanationsOption}
                  message={message}
                  setMessage={setMessage}
                />
              </>
            ) : (
              <div className='assignment-body u-display_flex u-flex-direction_row'>
                <div className='settings-questions__view-wrapper'>
                  <div className='settings-questions__create-a-class-container'>
                    <div className='u-text-align_center'>
                      <Text as='div' bold className='u-mar-t_4 u-mar-b_2'>
                        You have no classes
                      </Text>
                      <Text as='div' className='u-mar-b_2'>
                        Create a classroom to add recipients to your assignment.
                      </Text>
                      <Button
                        variant='outlined'
                        size='m'
                        onClick={() => setShowNewClassModal(true)}
                      >
                        Create A Class
                        <Icon icon='plus' className='u-mar-l_1' />
                      </Button>
                      {showNewClassModal && (
                        <NewClassroomModal
                          handleClose={() => {
                            setShowNewClassModal(false);
                          }}
                          onSubmit={() => setHasActiveClassrooms(true)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className='cd-modal__footer' role='group'>
            <Button
              variant='outlined'
              className='u-mar-r_1'
              onClick={() => {
                trackEvent({content: 'cancel'});
                handleClose();
              }}
            >
              Cancel
            </Button>
            {totalQuestionCount > 0 && (
              <Button data-test-id='AB-settings-modal__assign-btn' onClick={saveAssignment}>
                Assign
              </Button>
            )}
          </div>
        </Dialogue>
      )}
    </Modal>
  );
};

export default SettingsModal;
